import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UsersTable } from '@/components/users/UsersTable';
import { UserFilters, type UserFiltersState } from '@/components/users/UserFilters';
import { apiClient } from '@/services/api-client';
import type { User, PaginatedResponse } from '@/types';

export function UsersPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<UserFiltersState>({});

  const {
    data: usersData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'users', page, filters],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        limit: '10',
      };
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.status) params.status = filters.status;

      const response = await apiClient.get<PaginatedResponse<User>>('/api/v1/admin/users', params);
      if (!response.success) throw new Error(response.error?.message);

      // The API returns items in data and meta in top-level meta
      return {
        items: (response.data as unknown as User[]) || [],
        meta: response.meta || { page: 1, limit: 10, total: 0, totalPages: 0 },
      } as PaginatedResponse<User>;
    },
  });

  const handleFiltersChange = (newFilters: UserFiltersState) => {
    setFilters(newFilters);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl font-bold text-foreground">Users</h1>
        <p className="text-muted-foreground">Manage and view all users on the platform</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <UserFilters filters={filters} onFiltersChange={handleFiltersChange} />
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display">All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <UsersTable
            data={usersData || null}
            isLoading={isLoading}
            page={page}
            onPageChange={setPage}
            onRefresh={() => refetch()}
          />
        </CardContent>
      </Card>
    </div>
  );
}
